import React, { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import Select from "react-select";
import { Rating } from "react-simple-star-rating";
import { useSelector } from "react-redux";
import axios from "axios";

const Support = () => {
  const userData = useSelector((state) => state.userSlice);
  const configData = useSelector((state) => state.configSlice);

  const typeOptions = [
    { value: "feedback", label: "Feedback" },
    { value: "query", label: "Query" },
  ];
  const htmlPattern = /<[^>]*>/;
  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .trim()
      .required("Name is required.")
      .max(100, "Name can not be more than 100 characters.")
      .test("NameTest", "Name is invalid", (value) => !htmlPattern.test(value)),
    email: Yup.string()
      .trim()
      .required("Email is required.")
      .email("Invalid email address.")
      .max(250, "Email can not be more than 250 characters."),
    phone: Yup.string()
      .trim()
      .required("Phone number is required.")
      .max(15, "Phone can not be more than 15 characters.")
      .matches(/^[0-9+-]+$/, "Phone number can only contain numbers, plus (+), and hyphen (-)."),
    type: Yup.object().shape({
      label: Yup.string(),
      value: Yup.string(),
    }),
    rating: Yup.number().when("type", {
      is: (type) => type?.value?.toLowerCase() === "feedback",
      then: () => Yup.number().required("Rating is required."),
    }),
    subject: Yup.string().when("type", {
      is: (type) => type?.value?.toLowerCase() === "query",
      then: () =>
        Yup.string()
          .trim()
          .required("Query is required.")
          .max(250, "Subject can not be more than 250 characters.")
          .test("SubjectTest", "Subject is invalid", (value) => !htmlPattern.test(value)),
    }),
    description: Yup.string()
      .optional()
      .trim()
      .max(5000, "Subject can not be more than 5000 characters.")
      .test("DescriptionTest", "Description is invalid", (value) => !htmlPattern.test(value)),
  });

  const formik = useFormik({
    initialValues: {
      name: "",
      email: "",
      phone: "",
      type: typeOptions.find((option) => option.value === "query"),
      rating: 0,
      subject: "",
      description: "",
    },
    validationSchema: validationSchema,

    onSubmit: async (values) => {
      try {
        let data = {
          name: values.name,
          email: values.email,
          phone: values.phone,
          type: values.type.label,
          rating: values.rating,
          subject: values.subject,
          description: values.description,
          applicationId: configData.config.applicationId,
        };

        await axios.post(`${configData.config.UNO_URL}/api/user/support`, data, {
          headers: {
            Authorization: `Bearer ${userData.token}`,
            "Content-Type": "application/json",
          },
        });
      } catch (error) {
        console.log(error);
      } finally {
        formik.resetForm();
      }
    },
  });

  const debounce = (func, delay) => {
    let timer;
    return function (...args) {
      clearTimeout(timer);
      timer = setTimeout(() => func.apply(this, args), delay);
    };
  };

  const debouncedSendReport = debounce(formik.handleSubmit, 1000); // Adjust debounce delay as needed

  const resetButton = () => {
    formik.resetForm();
  };

  return (
    <>
      <div className="container-fluid bg-grey-main contactpage p-3 px-4">
        <h4>We would be happy to assist.</h4>
        <h6>Send a message, and we'll connect.</h6>
        <div className="row">
          <div className="col-md-6">
            <div className="quesformbox mt-2">
              <p>
                Name <span>*</span>{" "}
              </p>
              <input
                name="name"
                placeholder="Full Name"
                type="text"
                className={`input mt-2 ${
                  formik.touched.name && formik.errors.name ? "is-invalid" : ""
                }`}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.name}
              />
              {formik.touched.name && formik.errors.name && (
                <div className="invalid-feedback">{formik.errors.name}</div>
              )}
            </div>
          </div>

          <div className="col-md-6">
            <div className="quesformbox mt-2">
              <p>
                Email <span>*</span>
              </p>
              <input
                name="email"
                placeholder="youremail@example.com"
                type="text"
                className={`input mt-2 ${
                  formik.touched.email && formik.errors.email ? "is-invalid" : ""
                }`}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.email}
              />
              {formik.touched.email && formik.errors.email && (
                <div className="invalid-feedback">{formik.errors.email}</div>
              )}
            </div>
          </div>

          <div className="col-md-6">
            <div className="quesformbox mt-2">
              <p>
                Phone<span>*</span>
              </p>
              <input
                name="phone"
                placeholder="Contact Number"
                type="text"
                className={`input mt-2 ${
                  formik.touched.phone && formik.errors.phone ? "is-invalid" : ""
                }`}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.phone}
              />
              {formik.touched.phone && formik.errors.phone && (
                <div className="invalid-feedback">{formik.errors.phone}</div>
              )}
            </div>
          </div>

          <div className="col-md-6">
            <div className="quesformbox mt-2">
              <p>Type</p>
              <div className="input-group">
                <Select
                  className="basic-single mt-2"
                  classNamePrefix="select"
                  value={formik.values.type}
                  onChange={(selectedOption) => {
                    formik.setFieldValue("type", selectedOption);
                    formik.setFieldValue("rating", 0);
                    formik.setFieldValue("subject", "");
                  }}
                  onBlur={() => formik.setFieldTouched("type", true)}
                  options={typeOptions}
                />
                {formik.touched.type && formik.errors.type && (
                  <div className="error">{formik.errors.type.label}</div>
                )}
              </div>
            </div>
          </div>

          <div className="col-md-12">
            {formik.values.type?.value === "feedback" ? (
              <>
                <div className="quesformbox mt-2 rate">
                  <p className="mb-0">Ratings</p>
                  <Rating
                    onClick={(rate) => {
                      formik.setFieldValue("rating", rate);
                      formik.setFieldTouched("rating", true);
                    }}
                    initialValue={formik.values.rating}
                  />
                  {formik.touched.rating && formik.errors.rating && (
                    <div className="error">{formik.errors.rating}</div>
                  )}
                </div>
              </>
            ) : (
              <div className="quesformbox mt-2">
                <p>Subject</p>
                <input
                  name="subject"
                  placeholder="Subject"
                  type="text"
                  className={`input mt-2 ${
                    formik.touched.subject && formik.errors.subject ? "is-invalid" : ""
                  }`}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.subject}
                />
                {formik.touched.subject && formik.errors.subject && (
                  <div className="invalid-feedback">{formik.errors.subject}</div>
                )}
              </div>
            )}
          </div>

          <div className="col-md-12">
            <div className="quesformbox mt-2">
              <p>Description </p>
              <textarea
                name="description"
                placeholder="Description"
                type="text"
                className={`input mt-2 ${
                  formik.touched.description && formik.errors.description ? "is-invalid" : ""
                }`}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.description}
              />
              {formik.touched.description && formik.errors.description && (
                <div className="invalid-feedback">{formik.errors.description}</div>
              )}
            </div>
          </div>

          <div className="col-12">
            <div className="footerbtn text-end mt-4">
              <button onClick={resetButton} className="cancel">
                Cancel
              </button>
              {formik?.isSubmitting ? (
                <button className="loading save">
                  <svg viewBox="25 25 50 50">
                    <circle r="20" cy="50" cx="50"></circle>
                  </svg>
                </button>
              ) : (
                <button onClick={debouncedSendReport} className="save" type="submit">
                  Submit
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Support;
